/**
 * Money — o tipo de dinheiro do DigitalView.
 *
 * A proibição da spec 01 §1 ("`float`/`double` para dinheiro") não é preciosismo de
 * engenharia: em ponto flutuante binário 0,1 + 0,2 não dá 0,3, e um centavo perdido
 * por linha é como duas telas passam a mostrar números diferentes para a mesma
 * coisa — que é exatamente o que este sistema existe para não ter.
 *
 * Três decisões que valem explicação:
 *
 * 1. **`Money.de` NÃO aceita `number`.** Aceitar seria aceitar um valor que já pode
 *    ter chegado errado — `0.1 + 0.2` vira `0.30000000000000004` antes de a
 *    biblioteca ver qualquer coisa. Entra `string` ou `Decimal`, e o compilador
 *    cobra.
 * 2. **Somar moedas diferentes lança erro**, nunca converte por conta própria. A
 *    conversão precisa de uma data e de uma cotação (spec 05 §2, `fn_to_base`), e
 *    adivinhar qualquer uma das duas produz um número que ninguém confere.
 * 3. **Arredondamento comercial (meio para cima) e escala da MOEDA.** A escala vem
 *    de `Moeda.casas`, não de uma constante 2 — CLP e PYG não têm centavos.
 */
import { Decimal } from 'decimal.js'
import { mesmaMoeda, type Moeda } from './moeda.js'

/**
 * Construtor local em vez de configurar o `Decimal` global: mexer na configuração
 * global afetaria qualquer outra biblioteca que use decimal.js no mesmo processo.
 * `toExpNeg`/`toExpPos` afastados para que `toString()` nunca devolva notação
 * científica — um valor monetário que vira "1e+21" numa serialização é um defeito
 * caro e silencioso.
 */
const Dec = Decimal.clone({
  precision: 34,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -30,
  toExpPos: 30,
})

export class MoedasIncompativeis extends Error {
  constructor(a: Moeda, b: Moeda, operacao: string) {
    super(
      `${operacao} entre moedas diferentes (${a.iso} e ${b.iso}) é proibido. ` +
        'Converta antes com a cotação vigente da data (spec 05 §2) — somar moedas sem câmbio é como o número errado nasce.',
    )
    this.name = 'MoedasIncompativeis'
  }
}

export class QuantiaInvalida extends Error {
  constructor(entrada: unknown) {
    super(`quantia inválida para Money: ${JSON.stringify(entrada)}`)
    this.name = 'QuantiaInvalida'
  }
}

export class Money {
  readonly quantia: Decimal
  readonly moeda: Moeda

  private constructor(quantia: Decimal, moedaDoValor: Moeda) {
    this.quantia = quantia
    this.moeda = moedaDoValor
  }

  /** `string` ou `Decimal` — nunca `number` (ver o cabeçalho deste arquivo). */
  static de(quantia: string | Decimal, moedaDoValor: Moeda): Money {
    // O `DecimalError` da biblioteca não atravessa a fronteira do domínio: quem
    // chama lida com UM tipo de erro nosso, e trocar decimal.js um dia não muda o
    // contrato de quem depende daqui.
    let valor: Decimal
    try {
      valor = quantia instanceof Decimal ? new Dec(quantia.toString()) : new Dec(quantia)
    } catch {
      throw new QuantiaInvalida(quantia)
    }
    if (!valor.isFinite()) throw new QuantiaInvalida(quantia)
    return new Money(valor, moedaDoValor)
  }

  static zero(moedaDoValor: Moeda): Money {
    return new Money(new Dec(0), moedaDoValor)
  }

  private exigirMesmaMoeda(outro: Money, operacao: string): void {
    if (!mesmaMoeda(this.moeda, outro.moeda)) throw new MoedasIncompativeis(this.moeda, outro.moeda, operacao)
  }

  somar(outro: Money): Money {
    this.exigirMesmaMoeda(outro, 'soma')
    return new Money(this.quantia.plus(outro.quantia), this.moeda)
  }

  subtrair(outro: Money): Money {
    this.exigirMesmaMoeda(outro, 'subtração')
    return new Money(this.quantia.minus(outro.quantia), this.moeda)
  }

  /** Multiplica por um fator adimensional (percentual, quantidade, peso de rateio). */
  multiplicarPor(fator: string | Decimal): Money {
    return new Money(this.quantia.times(new Dec(fator.toString())), this.moeda)
  }

  negativo(): Money {
    return new Money(this.quantia.negated(), this.moeda)
  }

  /** Arredonda para a escala da MOEDA, meio para cima. */
  arredondar(): Money {
    return new Money(this.quantia.toDecimalPlaces(this.moeda.casas, Decimal.ROUND_HALF_UP), this.moeda)
  }

  /** −1, 0 ou 1. Comparar moedas diferentes é erro, pela mesma razão de somar. */
  comparar(outro: Money): number {
    this.exigirMesmaMoeda(outro, 'comparação')
    return this.quantia.comparedTo(outro.quantia)
  }

  igualA(outro: Money): boolean {
    return this.comparar(outro) === 0
  }

  maiorQue(outro: Money): boolean {
    return this.comparar(outro) > 0
  }

  menorQue(outro: Money): boolean {
    return this.comparar(outro) < 0
  }

  maiorOuIgualA(outro: Money): boolean {
    return this.comparar(outro) >= 0
  }

  ehZero(): boolean {
    return this.quantia.isZero()
  }

  ehNegativo(): boolean {
    return this.quantia.isNegative() && !this.quantia.isZero()
  }

  /** O maior entre os dois — base do "piso 0 é normativo" da spec 05 §4. */
  static maiorDentre(a: Money, b: Money): Money {
    return a.maiorQue(b) ? a : b
  }

  /** Representação canônica, sem localização. Formatar é trabalho da borda. */
  paraString(): string {
    return `${this.quantia.toFixed(this.moeda.casas)} ${this.moeda.iso}`
  }
}
